import { translateError } from '@/i18n/errors';
import { ref, computed } from 'vue';
import { makeLogger } from '@/services/utils/logger';
import { LOGGING } from '@/config';
import { translate } from '@/i18n';
const DEBUG_LOG = false;
const log = makeLogger('User', DEBUG_LOG, LOGGING.AUTH_LOG);
import AuthService from './AuthService';

export default class User {
  /**
   * Конструктор класса User. Инициализирует свойства пользователя.
   */
  constructor() {
    const raw = this.#getUserFromStorage();
    this._publicId = ref(raw?._publicId ?? null);
    this._dbId = raw?._dbId ?? null;
    this._clubId = raw?._clubId ?? null;
    this._clubSlug = raw?._clubSlug ?? null;
    this._clubName = raw?._clubName ?? null;
    this._username = raw?._username ?? null;
    this._email = raw?._email ?? null;
    this._role = ref(raw?._role ?? null);
  }

  /**
   * Выполняет авторизацию пользователя.
   * @param {string} identifier - Имя пользователя или email.
   * @param {string} password - Пароль пользователя.
   * @returns {Promise<{user: Object|null, success: boolean, error?: any, message?: string}>}
   */
  async login(identifier, password) {
    try {
      const jwt= await AuthService.getToken(identifier, password);

      localStorage.setItem('token', jwt);

      const user = await AuthService.getMe();
      
      localStorage.setItem('user', JSON.stringify(user));
      this.fromObject(user);

      log('login', '✅ User', this);

      return { user: this, success: true };
    } catch (e) {
      log.error('login', '❌ Ошибка авторизации', e);
      log.error('login', '❌ User', this);
      
      this._publicId.value = null;
      this._dbId = null;
      this._clubId = null;
      this._clubSlug = null;
      this._clubName = null;
      this._username = null;
      this._email = null;
      this._role.value = null;

      return {
        user: null,
        error: e,
        message: translateError(e, translate('auth.loginError')),
        success: false
      };
    }
  }

  /**
   * Выполняет выход пользователя и очищает локальное хранилище.
   */
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    this._publicId.value = null;
    this._dbId = null;
    this._clubId = null;
    this._clubSlug = null;
    this._clubName = null;
    this._username = null;
    this._email = null;
    this._role.value = null;
  }

  /**
   * Возвращает токен авторизации из локального хранилища.
   * @returns {string|null}
   */
  getToken() {
    return localStorage.getItem('token');
  }

  /**
   * Геттер для получения documentId текущего пользователя.
   * @returns {string|null}
   */
  get id() {
    return this._publicId.value;
  }

  /**
   * Геттер для получения Strapi id текущего пользователя.
   * @returns {string|null}
   */
  get dbId() {
    return this._dbId;
  }

  /**
   * Геттер для определения, авторизован ли пользователь.
   * @returns {boolean}
   */
  get isAuthenticated() {
    return computed(() => {
      const hasId = !!this._publicId.value;
      const hasToken = !!this.getToken();
      return hasId && hasToken;
    });
  }

  /**
   * Геттер для получения clubId текущего пользователя.
   * @returns {string|null}
   */
  get clubId() {
    return this._clubId;
  }

  /**
   * Геттер для получения slug клуба текущего пользователя.
   * @returns {string|null}
   */
  get clubSlug() {
    return this._clubSlug;
  }

  /**
   * Геттер для получения названия клуба текущего пользователя.
   * @returns {string|null}
   */
  get clubName() {
    return this._clubName;
  }

  /**
   * Геттер для получения username текущего пользователя.
   * @returns {string|null}
   */
  get username() {
    return this._username;
  }

  /**
   * Геттер для получения email текущего пользователя.
   * @returns {string|null}
   */
  get email() {
    return this._email;
  }

  /**
   * Геттер для получения роли текущего пользователя.
   * @returns {{name:string|null,type:string|null}|null}
   */
  get role() {
    return this._role.value;
  }

  /**
   * Проверяет, является ли пользователь superadmin.
   * @returns {boolean}
   */
  get isSuperAdmin() {
    const roleValues = [
      this._role.value?.type,
      this._role.value?.name,
    ]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());

    return roleValues.includes('superadmin');
  }

  /**
   * Обновляет свойства пользователя из объекта.
   * @param {Object} user
   */
  fromObject(user) {
    this._publicId.value = user._publicId ?? null;
    this._dbId = user._dbId ?? null;
    this._clubId = user._clubId ?? null;
    this._clubSlug = user._clubSlug ?? null;
    this._clubName = user._clubName ?? null;
    this._username = user._username ?? null;
    this._email = user._email ?? null;
    this._role.value = user._role ?? null;
  }

  /**
   * Обновляет кэш пользователя в localStorage.
   */
  updateCache() {
    localStorage.setItem('user', JSON.stringify({
      _publicId: this._publicId.value,
      _dbId: this._dbId,
      _clubId: this._clubId,
      _clubSlug: this._clubSlug,
      _clubName: this._clubName,
      _username: this._username,
      _email: this._email,
      _role: this._role.value
    }));
  }

  /**
   * Получает пользователя из localStorage.
   * @returns {Object|null}
   * @private
   */
  #getUserFromStorage() {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  }
}
